import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import axios from "../../axiosConfig";
import "../../styles/PendingRequestModal.css";
import { FiTrash2 } from "react-icons/fi";
import DeleteConfirmationModal from "./DeleteConfirmationModal";

const PendingRequestsModal = ({ trainerId, schoolId, slotTime, slotEndTime, onClose, refreshCalendar, allowReserveList }) => {
    const { t } = useTranslation('trainings');
    const [requests, setRequests] = useState([]);
    const [selectedTraining, setSelectedTraining] = useState(null);
    const [loading, setLoading] = useState(false);

    // NEW: State for halls and selected hall from dropdown
    const [halls, setHalls] = useState([]);
    const [selectedHallId, setSelectedHallId] = useState(null);

    // NEW: State for delete confirmation modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);

    // Fetch pending requests for the given slot
    useEffect(() => {
        if (!trainerId || !slotTime) return;
        const startTime = slotTime.format("YYYY-MM-DDTHH:mm:ss");
        const endTime = slotEndTime
            ? slotEndTime.format("YYYY-MM-DDTHH:mm:ss")
            : slotTime.clone().add(60, "minute").format("YYYY-MM-DDTHH:mm:ss");

        axios.get(`/api/v1/training/pending-requests/${trainerId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` },
            params: { startTime, endTime }
        })
            .then(res => setRequests(res.data || []))
            .catch(err => console.error("Error fetching pending requests:", err));
    }, [trainerId, slotTime, slotEndTime]);

    // Fetch available halls for the school with time parameters
    useEffect(() => {
        if (!schoolId || !slotTime) return;
        const token = localStorage.getItem("jwt");

        // Calculate timeFrom and timeTo for the slot
        const timeFrom = slotTime.format("YYYY-MM-DDTHH:mm:ss");
        const timeTo = slotTime.clone().add(1, "hour").format("YYYY-MM-DDTHH:mm:ss");

        axios.get(`/api/v1/school/${schoolId}/halls/available`, {
            headers: { Authorization: `Bearer ${token}` },
            params: {
                timeFrom,
                timeTo
            }
        })
            .then(res => {
                const hallsData = res.data || [];
                setHalls(hallsData);
                if (hallsData.length > 0) {
                    setSelectedHallId(hallsData[0].id); // default to first hall
                }
            })
            .catch(err => console.error("Error fetching available halls:", err));
    }, [schoolId, slotTime]);

    const handleConfirm = async () => {
        if (!selectedTraining) return;
        setLoading(true);
        try {
            // If halls exist, send the confirm request with hall selection
            if (halls.length > 0) {
                const payload = {
                    trainingId: selectedTraining,
                    hallId: selectedHallId,
                };
                await axios.patch(`/api/v1/training/confirm`, payload, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` }
                });
            } else {
                // Otherwise, use the old confirm endpoint
                await axios.patch(`/api/v1/training/${selectedTraining}/confirm`, null, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` }
                });
                if (!allowReserveList) {
                    const otherRequests = requests.filter(req => req.trainingId !== selectedTraining);
                    await Promise.all(
                        otherRequests.map(req =>
                            axios.post(`/api/v1/training/${req.trainingId}/decline`, null, {
                                headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` }
                            })
                        )
                    );
                }
            }
            refreshCalendar(); // Refresh calendar to re-fetch data
            onClose(); // Close the modal
        } catch (err) {
            console.error("Error confirming training:", err);
            alert(t('confirmError'));
        } finally {
            setLoading(false);
        }
    };

    // NEW: Handle opening delete confirmation modal
    const handleDeleteClick = (request, e) => {
        e.stopPropagation();
        setRequestToDelete(request);
        setDeleteModalOpen(true);
    };

    // NEW: Handle confirmed deletion
    const handleConfirmDelete = async () => {
        if (!requestToDelete) return;

        try {
            await axios.post(`/api/v1/training/${requestToDelete.trainingId}/decline`, null, {
                headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` }
            });
            // Remove the deleted request from the list
            setRequests(prev => prev.filter(req => req.trainingId !== requestToDelete.trainingId));
            refreshCalendar();

            // Close the delete modal and clear the request to delete
            setDeleteModalOpen(false);
            setRequestToDelete(null);
        } catch (err) {
            console.error("Error deleting pending request:", err);
            alert(t('deleteRequestError'));
        }
    };

    // NEW: Handle cancel deletion
    const handleCancelDelete = () => {
        setDeleteModalOpen(false);
        setRequestToDelete(null);
    };

    // NEW: Get the display name for the item being deleted
    const getDeleteItemName = () => {
        if (!requestToDelete) return "";
        return `${t('bookingFor')} ${requestToDelete.dogName} (${requestToDelete.ownerName})`;
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content pending-requests-modal">
                <div className="modal-header">
                    <div className="modal-header-info">
                        <h2 className="modal-title">{t('pendingBookings')}</h2>
                        <div className="slot-time-info">
                            <span className="slot-time">{slotTime.format("HH:mm")}</span>
                            <span className="slot-day">{slotTime.format("dddd, DD MMMM")}</span>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}>✕</button>
                </div>

                <div className="modal-body">
                    {/* Hall Dropdown: Only render if halls exist */}
                    {halls.length > 0 && (
                        <div className="form-section">
                            <label htmlFor="hall-select" className="form-label">{t('selectHall')}</label>
                            <select
                                id="hall-select"
                                value={selectedHallId || ""}
                                onChange={(e) => setSelectedHallId(e.target.value)}
                                className="form-select"
                            >
                                {halls.map((hall) => (
                                    <option key={hall.id} value={hall.id}>
                                        {hall.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="requests-section">
                        {requests.length === 0 ? (
                            <div className="no-requests">
                                <div className="no-requests-icon">📅</div>
                                <p>{t('noPendingBookings')}</p>
                                <span>{t('noPendingBookingsDesc')}</span>
                            </div>
                        ) : (
                            <div className="requests-list">
                                <div className="requests-header">
                                    <span className="requests-count">{t('pendingBookingsCount', { count: requests.length })}</span>
                                </div>

                                <div className="requests-grid">
                                    {requests.map(request => (
                                        <div
                                            key={request.trainingId}
                                            className={`request-card ${selectedTraining === request.trainingId ? 'selected' : ''}`}
                                            onClick={() => setSelectedTraining(request.trainingId)}
                                        >
                                            <button
                                                className="delete-request-btn"
                                                onClick={(e) => handleDeleteClick(request, e)}
                                                title={t('deleteBooking')}
                                            >
                                                <FiTrash2 />
                                            </button>

                                            <div className="dog-name">{request.dogName}</div>
                                            <div className="dog-breed">{request.breed}</div>
                                            <div className="confirmed-trainings">{t('confirmedTrainings')}: {request.confirmedTrainingsCount}</div>

                                            <div className="owner-section">
                                                <div className="owner-label">{t('ownerInfo')}</div>
                                                <div className="owner-name">{request.ownerName}</div>
                                            </div>

                                            <div className="service-section">
                                                <div className="service-name">{request.serviceName}</div>
                                                <div className="service-duration">{t('duration')}: {request.serviceDuration}</div>
                                            </div>

                                            {request.additionalInfo && (
                                                <div className="additional-info-section">
                                                    <div className="additional-info-label">{t('additionalInfo')}</div>
                                                    <div className="additional-info-text">{request.additionalInfo}</div>
                                                </div>
                                            )}

                                            <div className="booking-time">
                                                {t('bookedAt')}: {dayjs(request.bookingTime).format("HH:mm:ss")}
                                            </div>

                                            {selectedTraining === request.trainingId && (
                                                <div className="selected-indicator">
                                                    <div className="selected-checkmark">✓</div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer INSIDE modal-content */}
                {requests.length > 0 && (
                    <div className="modal-footer">
                        <button
                            className="cancel-btn"
                            onClick={onClose}
                        >
                            {t('cancel')}
                        </button>
                        <button
                            className="confirm-btn"
                            onClick={handleConfirm}
                            disabled={!selectedTraining || loading}
                        >
                            {loading ? (
                                <>
                                    <span className="loading-spinner"></span>
                                    {t('confirming')}
                                </>
                            ) : (
                                t('confirm')
                            )}
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="loading-overlay">
                        <div className="loading-spinner"></div>
                        <span>{t('confirming')}</span>
                    </div>
                )}
            </div>

            {/* NEW: Delete Confirmation Modal */}
            <DeleteConfirmationModal
                isOpen={deleteModalOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                itemName={getDeleteItemName()}
            />
        </div>
    );
};

export default PendingRequestsModal;